'use client'

/**
 * One budget input: title, typed number, unit, slider with its default marked,
 * benchmark marks under the track, and a reset chip that appears when the
 * value leaves its default. Linear or log-scaled; the typed number is always
 * the real value. `tone` is the section's hue (CSS color).
 */

import { useRef, useState, type ReactNode } from 'react'
import * as SliderPrimitive from '@radix-ui/react-slider'
import { RotateCcw } from '@/components/ui/icons'
import { cn } from '@/lib/utils'
import { clamp, logToValue, valueToLog, LOG_DOMAINS, type NumericParam } from './model'
import type { FilmBudgetInputSource } from '@/lib/analytics'

export interface Mark {
  at: number
  label: string
}

export interface ControlProps {
  param: NumericParam
  title: string
  hint?: ReactNode
  tone: string
  value: number
  defaultValue: number
  min: number
  max: number
  step: number
  /** Unit shown after the number ("min", "%", "s"); a leading "$" goes before it via `prefix`. */
  unit?: string
  prefix?: string
  marks?: Mark[]
  onChange: (value: number, source: FilmBudgetInputSource) => void
  /** Called once per gesture (end of drag, blur of the number) with the value it started from. */
  onCommit: (from: number, to: number, source: FilmBudgetInputSource) => void
  disabled?: boolean
  children?: ReactNode
}

export function Control({
  param,
  title,
  hint,
  tone,
  value,
  defaultValue,
  min,
  max,
  step,
  unit,
  prefix,
  marks,
  onChange,
  onCommit,
  disabled,
  children,
}: ControlProps) {
  const isLog = param in LOG_DOMAINS
  const toPos = (v: number) => (isLog ? valueToLog(param, v) : v)
  const sliderMin = isLog ? 0 : min
  const sliderMax = isLog ? 100 : max
  const sliderStep = isLog ? 0.5 : step
  const pct = (v: number) => Math.max(0, Math.min(100, ((toPos(v) - sliderMin) / (sliderMax - sliderMin)) * 100))
  const atDefault = Math.abs(value - defaultValue) < 1e-9

  const dragFrom = useRef<number | null>(null)
  const focusFrom = useRef<number | null>(null)
  // The typed number keeps its own text while focused so "1." or "" don't
  // snap back mid-keystroke; it re-syncs from the value when not editing.
  const [draft, setDraft] = useState<string | null>(null)
  const ids = { title: `${param}-title`, hint: `${param}-hint` }

  const fmt = (v: number) => (Number.isInteger(v) ? String(v) : String(Math.round(v * 100) / 100))

  return (
    <div
      className={cn(
        'border-t border-white/10 pt-8 mt-8 first:mt-0 first:border-0 first:pt-0',
        disabled && 'opacity-40',
      )}
      style={{ '--tone': tone } as React.CSSProperties}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 pt-1">
          <div id={ids.title} className="text-base font-medium text-white">
            {title}
          </div>
          {hint && (
            <div id={ids.hint} className="mt-1 max-w-[52ch] text-sm leading-snug text-white/60">
              {hint}
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {!atDefault && (
            <button
              type="button"
              onClick={() => {
                onChange(defaultValue, 'reset')
                onCommit(value, defaultValue, 'reset')
              }}
              aria-label={`Reset to ${prefix ?? ''}${fmt(defaultValue)}${unit ? ` ${unit}` : ''}`}
              title="Reset to default"
              className="inline-flex h-7 items-center gap-1 rounded-md border border-white/10 px-2 font-mono text-xs text-white/55 transition-colors hover:border-white/25 hover:text-white motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200"
            >
              <RotateCcw className="h-3 w-3" aria-hidden />
              {prefix}
              {fmt(defaultValue)}
            </button>
          )}
          <label className="flex h-10 items-center rounded-lg border border-white/15 bg-white/[0.05] pe-3 ps-2.5 transition-colors focus-within:border-(--tone) focus-within:bg-white/[0.07] hover:border-white/25">
            {prefix && <span className="me-0.5 font-mono text-sm text-white/50">{prefix}</span>}
            <input
              type="number"
              inputMode="decimal"
              aria-labelledby={ids.title}
              aria-describedby={hint ? ids.hint : undefined}
              min={min}
              max={max}
              step={step}
              value={draft ?? fmt(value)}
              disabled={disabled}
              onFocus={e => {
                focusFrom.current = value
                setDraft(fmt(value))
                e.currentTarget.select()
              }}
              onChange={e => {
                setDraft(e.target.value)
                const next = Number(e.target.value)
                if (e.target.value !== '' && Number.isFinite(next)) onChange(clamp(param, next), 'number')
              }}
              onBlur={() => {
                setDraft(null)
                if (focusFrom.current !== null && focusFrom.current !== value) {
                  onCommit(focusFrom.current, value, 'number')
                }
                focusFrom.current = null
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') e.currentTarget.blur()
              }}
              className="w-[4.5rem] bg-transparent text-right font-mono text-lg font-semibold tabular-nums text-white outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
            {unit && <span className="ms-1.5 font-mono text-xs text-white/50">{unit}</span>}
          </label>
        </div>
      </div>

      <div className="relative mt-4">
        <SliderPrimitive.Root
          aria-labelledby={ids.title}
          min={sliderMin}
          max={sliderMax}
          step={sliderStep}
          value={[toPos(value)]}
          disabled={disabled}
          onValueChange={([pos]) => {
            if (dragFrom.current === null) dragFrom.current = value
            onChange(isLog ? logToValue(param, pos) : pos, 'slider')
          }}
          onValueCommit={([pos]) => {
            const to = isLog ? logToValue(param, pos) : pos
            const from = dragFrom.current ?? value
            dragFrom.current = null
            if (from !== to) onCommit(from, to, 'slider')
          }}
          className="relative flex h-8 w-full touch-none select-none items-center"
        >
          <SliderPrimitive.Track className="relative h-1.5 w-full grow rounded-full bg-white/[0.12]">
            <SliderPrimitive.Range className="absolute h-full rounded-full bg-(--tone)" />
          </SliderPrimitive.Track>
          {/* default marker — a tick under the track, hidden while the value sits on it */}
          {!atDefault && (
            <span
              aria-hidden
              className="pointer-events-none absolute top-[22px] h-2 w-px -translate-x-1/2 bg-white/40"
              style={{ left: `${pct(defaultValue)}%` }}
            />
          )}
          <SliderPrimitive.Thumb
            aria-labelledby={ids.title}
            className="block h-5 w-5 cursor-grab rounded-full border-[3px] border-(--tone) bg-[#141414] shadow-[0_1px_6px_rgba(0,0,0,.6)] transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--tone)/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#141414] active:cursor-grabbing active:scale-110"
          />
        </SliderPrimitive.Root>
        {marks && marks.length > 0 && (
          <div aria-hidden className="relative mt-1 h-10 text-xs leading-[1.3] text-white/50">
            {marks.map((m, i) => {
              const p = pct(m.at)
              const first = i === 0 && p === 0
              const last = i === marks.length - 1 && p === 100
              const lines = m.label.split('\n')
              // Phones: a mark closer than 14% to its neighbours would overlap, so it steps aside (first and last always show).
              const crowded =
                !first &&
                !last &&
                (p - pct(marks[i - 1].at) < 14 || (i < marks.length - 1 && pct(marks[i + 1].at) - p < 14))
              return (
                <span
                  key={m.at}
                  className={cn(
                    'absolute whitespace-nowrap',
                    crowded && 'max-sm:hidden',
                    first ? 'text-left' : last ? '-translate-x-full text-right' : '-translate-x-1/2 text-center',
                  )}
                  style={{ left: `${p}%` }}
                >
                  <span
                    className={cn('mb-1 block h-1.5 w-px bg-white/25', first ? 'ml-0' : last ? 'ml-auto' : 'mx-auto')}
                  />
                  {lines.map((line, j) => (
                    <span key={j} className={cn('block', j > 0 && 'text-white/35')}>
                      {line}
                    </span>
                  ))}
                </span>
              )
            })}
          </div>
        )}
      </div>
      {children}
    </div>
  )
}
